import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Button, Chip, CircularProgress, Dialog, Grow, IconButton, TextField } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonAddAlt1RoundedIcon from "@mui/icons-material/PersonAddAlt1Rounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import BackgroundLetterAvatarsLogo from "../../../components/AvatarLogo";
import "../../../App.css";
import "../../../components/Modal/modal.scss";
import "./addPeopleToProject.scss";
import useProjectRoles from "../../../common/useProjectRoles";
import { useToast } from "../../../components/Toast";
import API_BASE_URL from "../../../components/config/appConfig";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useDispatch, useSelector } from 'react-redux';
import { getActiveProjectData } from "../../../common/Redux/Reducer";
import { useParams } from "react-router-dom";

const SmoothGrow = React.forwardRef(function SmoothGrow(props, ref) {
  return <Grow ref={ref} timeout={{ enter: 320, exit: 200 }} {...props} />;
});

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_ROLE = "Developer";

export const isValidEmail = (value) => EMAIL_RE.test(String(value || "").trim());

/* one shape for "someone in the org" and "an email we will invite", so the
   picker, the chips and the request all read the same fields */
const fromUser = (user) => ({
  key: String(user.email || "").toLowerCase(),
  id: user._id,
  name: user.name || "",
  email: user.email,
  invite: false,
});

const fromEmail = (email) => {
  const clean = String(email).trim();
  return { key: clean.toLowerCase(), id: null, name: "", email: clean, invite: true };
};

const AddPeopleToProject = ({
  open,
  ProjectName,
  setOpenAddPeopleModel = () => {},
}) => {
  const toast = useToast();
  const dispatch = useDispatch();
  const { projectSlug } = useParams();
  const { Projects } = useSelector((state) => state.ProjamAppSlice);
  const project = Projects?.selectedProject?.selectedProjectData;
  const projectId = project?._id;
  const projectName = ProjectName || project?.title || "this project";

  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  /* the same roles the settings page manages, so the two cannot drift apart */
  const { roleNames } = useProjectRoles(projectId);

  useEffect(() => {
    if (!role && roleNames.length) {
      setRole(roleNames.includes(DEFAULT_ROLE) ? DEFAULT_ROLE : roleNames[0]);
    }
  }, [roleNames, role]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("accessToken");
        const response = await axios(`${API_BASE_URL}/users`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) setAllUsers(response?.data?.users || []);
      } catch (err) {
        console.log(err);
      } finally {
        if (!cancelled) setUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const memberEmails = useMemo(
    () => new Set((Projects?.selectedProject?.contributors || []).map((m) => String(m?.employeeEmail || "").toLowerCase())),
    [Projects?.selectedProject?.contributors]
  );

  const options = useMemo(
    () => allUsers.filter((user) => user?.email).map(fromUser),
    [allUsers]
  );

  const isMember = (person) => memberEmails.has(person.key);

  /* Matches on name or email, and offers an invite when what was typed is an
     email nobody in the list has. */
  const filterOptions = (list, { inputValue: typed }) => {
    const term = typed.trim().toLowerCase();
    const matches = term
      ? list.filter((p) => p.email.toLowerCase().includes(term) || p.name.toLowerCase().includes(term))
      : list;
    const exact = list.some((p) => p.key === term);
    const alreadyPicked = selected.some((p) => p.key === term);
    if (isValidEmail(term) && !exact && !alreadyPicked) {
      return [...matches, { ...fromEmail(typed), isInviteOption: true }];
    }
    return matches.slice(0, 50);
  };

  const handleChange = (_event, values) => {
    setError("");
    const next = [];
    values.forEach((value) => {
      if (typeof value === "string") {
        // Enter on free text: accept it only as an email
        const match = options.find((p) => p.key === value.trim().toLowerCase());
        if (match) next.push(match);
        else if (isValidEmail(value)) next.push(fromEmail(value));
        else setError(`"${value}" is not a valid email address`);
      } else {
        next.push(value.isInviteOption ? fromEmail(value.email) : value);
      }
    });
    // no duplicates, and nobody already on the project
    const seen = new Set();
    setSelected(next.filter((p) => {
      if (seen.has(p.key)) return false;
      seen.add(p.key);
      if (isMember(p)) {
        setError(`${p.name || p.email} is already on this project`);
        return false;
      }
      return true;
    }));
  };

  const canSubmit = selected.length > 0 && Boolean(role) && !saving;

  const handleClose = () => {
    if (!saving) setOpenAddPeopleModel(false);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");

    let invitedBy;
    try {
      invitedBy = jwtDecode(localStorage.getItem("accessToken") || "")?.id;
    } catch {
      invitedBy = undefined;
    }

    // one at a time: the endpoint takes a single member, and each one sends mail
    const failed = [];
    const added = [];
    for (const person of selected) {
      try {
        await axios.post(
          `${API_BASE_URL}/projects/addmember?projectid=${projectId}`,
          { invitedBy, employeeId: person.id || undefined, employeeEmail: person.email, role },
          { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
        );
        added.push(person);
      } catch (err) {
        failed.push({ person, message: err?.response?.data?.message || "Could not be added" });
      }
    }

    if (added.length) {
      dispatch(getActiveProjectData(projectSlug || project?.slugString));
      const who = added.length === 1 ? (added[0].name || added[0].email) : `${added.length} people`;
      toast.success(`${who} added to ${projectName} as ${role}`);
    }
    setSaving(false);

    if (failed.length) {
      // keep only what failed, so a retry does not add anyone twice
      setSelected(failed.map((f) => f.person));
      setError(failed.map((f) => `${f.person.name || f.person.email}: ${f.message}`).join(" · "));
      return;
    }
    setOpenAddPeopleModel(false);
  };

  const inviteCount = selected.filter((p) => p.invite).length;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="add-people-dialog-title"
      maxWidth="sm"
      fullWidth
      TransitionComponent={SmoothGrow}
      PaperProps={{ className: "pj-modal pj-modal--form pj-modal--add-people", component: "form", onSubmit: handleSubmit, noValidate: true }}
      slotProps={{ backdrop: { className: "pj-modal-backdrop" } }}
    >
      <div className="pj-modal-body">
        <IconButton className="pj-modal-close" size="small" onClick={handleClose} aria-label="Close" disabled={saving}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>

        <div className="pj-modal-head">
          <span className="pj-modal-badge">
            <PersonAddAlt1RoundedIcon />
          </span>
          <div>
            <h3 className="pj-modal-title" id="add-people-dialog-title">
              Add team members
            </h3>
            <p className="pj-modal-sub">
              Add people to <strong>{projectName}</strong>. Anyone without an account gets an email invite.
            </p>
          </div>
        </div>

        <div className="pj-field-group">
          <label className="pj-field-label" htmlFor="add-people-search">
            People<span className="pj-req">*</span>
          </label>
          <Autocomplete
            id="add-people-search"
            multiple
            freeSolo
            filterSelectedOptions
            options={options}
            value={selected}
            loading={usersLoading}
            inputValue={inputValue}
            onInputChange={(_e, value) => setInputValue(value)}
            onChange={handleChange}
            filterOptions={filterOptions}
            getOptionLabel={(option) => (typeof option === "string" ? option : option.name || option.email)}
            isOptionEqualToValue={(option, value) => option.key === value.key}
            getOptionDisabled={(option) => !option.isInviteOption && isMember(option)}
            disabled={saving}
            slotProps={{ paper: { className: "add-people-menu" } }}
            renderOption={(props, option) => {
              const { key, ...rest } = props;
              const member = !option.isInviteOption && isMember(option);
              return (
                <li key={option.isInviteOption ? `invite-${option.key}` : option.key} {...rest} className={`${rest.className} add-people-option`}>
                  {option.isInviteOption ? (
                    <span className="add-people-option__invite"><MailOutlineRoundedIcon /></span>
                  ) : (
                    <BackgroundLetterAvatarsLogo LogoTitle={option.name || option.email} RequiredFontSize="11px !important" />
                  )}
                  <span className="add-people-option__text">
                    <span className="add-people-option__name">
                      {option.isInviteOption ? `Invite ${option.email}` : option.name || option.email}
                    </span>
                    <span className="add-people-option__email">
                      {option.isInviteOption ? "They'll get an email to join" : option.email}
                    </span>
                  </span>
                  {member ? <span className="add-people-option__tag">Already on project</span> : null}
                </li>
              );
            }}
            renderTags={(values, getTagProps) =>
              values.map((person, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={person.key}
                    {...tagProps}
                    size="small"
                    className={`add-people-chip ${person.invite ? "is-invite" : ""}`}
                    icon={person.invite ? <MailOutlineRoundedIcon /> : undefined}
                    label={person.name || person.email}
                    title={person.email}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                placeholder={selected.length ? "Add another…" : "Search by name or email"}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </div>

        <div className="pj-field-group">
          <span className="pj-field-label" id="add-people-role-label">
            Role<span className="pj-req">*</span>
          </span>
          <div className="add-people-roles" role="radiogroup" aria-labelledby="add-people-role-label">
            {roleNames.map((name) => (
              <button
                key={name}
                type="button"
                role="radio"
                aria-checked={role === name}
                className={`add-people-role ${role === name ? "is-selected" : ""}`}
                onClick={() => setRole(name)}
                disabled={saving}
              >
                {role === name ? <CheckRoundedIcon /> : null}
                {name}
              </button>
            ))}
          </div>
        </div>

        {error ? <p className="add-people-error" role="alert">{error}</p> : null}
      </div>

      <div className="pj-modal-actions">
        <Button className="pj-btn pj-btn--quiet" type="button" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button className="pj-btn pj-btn--primary" type="submit" disabled={!canSubmit}>
          {saving
            ? "Adding…"
            : selected.length > 1
              ? `Add ${selected.length} people`
              : inviteCount === 1
                ? "Send invite"
                : "Add member"}
        </Button>
      </div>
    </Dialog>
  );
};

export default AddPeopleToProject;
